import React from "react";
import { Link } from "react-router-dom";
import { createPageStyles } from "./MarketingPageStyles";
import { detectPageLocale } from "./pageLocale";

const styles = createPageStyles("dark", "luna");

type Locale = "ko" | "en" | "ja";

const LOCALE_STORAGE_KEY = "lunatarot-terms-locale";
const locales: Locale[] = ["ko", "en", "ja"];
const localeLabels: Record<Locale, string> = { ko: "KO", en: "EN", ja: "JA" };

const content: Record<
  Locale,
  {
    crumb: string;
    title: string;
    accentTitle: string;
    subtitle: string;
    sections: Array<{ title: string; body?: string; list?: string[] }>;
  }
> = {
  ko: {
    crumb: "LunaTarot Terms",
    title: "LunaTarot,",
    accentTitle: "이용약관",
    subtitle:
      "최종 업데이트일: 2026년 5월 18일. 본 서비스의 운영 주체는 Daydream Factory입니다. LunaTarot 앱과 관련 웹페이지를 이용하는 경우 아래 약관이 적용됩니다.",
    sections: [
      {
        title: "1. 서비스 소개",
        body:
          "LunaTarot는 타로 리딩, 저널 기록, 선택적 AI 분석 기능을 제공하는 모바일 애플리케이션과 관련 랜딩 페이지로 구성됩니다.",
      },
      {
        title: "2. 이용 조건",
        list: [
          "사용자는 관련 법령을 위반하거나 서비스 운영을 방해하는 방식으로 서비스를 이용해서는 안 됩니다.",
          "비정상적 접근, 자동화 남용, 앱 또는 웹사이트의 역분석 시도는 제한될 수 있습니다.",
          "AI 분석 기능은 참고용 결과를 제공하며, 법률·의료·재정적 판단을 대신하지 않습니다.",
        ],
      },
      {
        title: "3. 지식재산권",
        list: [
          "앱 내 카드 이미지, 텍스트, 디자인, 로고 및 화면 구성에 대한 권리는 Daydream Factory 또는 정당한 권리자에게 귀속됩니다.",
          "사전 허가 없이 서비스 콘텐츠를 복제, 배포, 상업적으로 이용할 수 없습니다.",
        ],
      },
      {
        title: "4. 서비스 변경 및 중단",
        body:
          "Daydream Factory는 서비스 개선, 운영상 필요, 외부 API 또는 플랫폼 정책 변화 등에 따라 기능을 변경하거나 일부 서비스를 중단할 수 있습니다.",
      },
      {
        title: "5. 면책",
        list: [
          "타로 해석 및 AI 분석은 개인적 기록과 성찰을 돕기 위한 참고 정보입니다.",
          "외부 스토어 정책, 네트워크 환경, 외부 API 상태에 따라 일부 기능 제공이 제한될 수 있습니다.",
        ],
      },
      {
        title: "6. 문의",
        body: "약관 관련 문의는 아래 이메일로 접수할 수 있습니다: support@daydreamfactory.net",
      },
    ],
  },
  en: {
    crumb: "LunaTarot Terms",
    title: "LunaTarot,",
    accentTitle: "Terms of Use",
    subtitle:
      "Last updated: May 18, 2026. This service is operated by Daydream Factory. These terms apply to the LunaTarot app and its related web pages.",
    sections: [
      {
        title: "1. Service Overview",
        body:
          "LunaTarot consists of a mobile app and related landing pages that provide tarot reading, journaling, and optional AI analysis features.",
      },
      {
        title: "2. Conditions of Use",
        list: [
          "Users must not use the service in ways that violate applicable law or interfere with service operations.",
          "Abnormal access, abusive automation, or reverse-engineering attempts against the app or website may be restricted.",
          "AI analysis is provided for reflective reference only and does not replace legal, medical, or financial judgment.",
        ],
      },
      {
        title: "3. Intellectual Property",
        list: [
          "Rights to card artwork, app text, design, logos, and interface elements belong to Daydream Factory or the respective rights holders.",
          "You may not reproduce, distribute, or commercially use service content without prior permission.",
        ],
      },
      {
        title: "4. Service Changes and Suspension",
        body:
          "Daydream Factory may change features or suspend parts of the service due to product improvement, operational needs, or changes in external APIs or platform policies.",
      },
      {
        title: "5. Disclaimer",
        list: [
          "Tarot interpretations and AI analysis are intended only as reflective support for personal journaling.",
          "Some features may be limited by store policies, network conditions, or the availability of external APIs.",
        ],
      },
      {
        title: "6. Contact",
        body: "For questions about these terms, contact: support@daydreamfactory.net",
      },
    ],
  },
  ja: {
    crumb: "LunaTarot Terms",
    title: "LunaTarot,",
    accentTitle: "利用規約",
    subtitle:
      "最終更新日: 2026年5月18日。本サービスの運営主体は Daydream Factory です。LunaTarot アプリおよび関連ウェブページの利用には以下の規約が適用されます。",
    sections: [
      {
        title: "1. サービス概要",
        body:
          "LunaTarot は、タロットリーディング、ジャーナル記録、任意の AI 分析機能を提供するモバイルアプリと関連ランディングページで構成されます。",
      },
      {
        title: "2. 利用条件",
        list: [
          "利用者は、法令違反またはサービス運営を妨げる方法で本サービスを利用してはなりません。",
          "異常なアクセス、自動化の乱用、アプリやウェブサイトへのリバースエンジニアリング行為は制限される場合があります。",
          "AI 分析は参考情報として提供され、法務・医療・財務判断の代替にはなりません。",
        ],
      },
      {
        title: "3. 知的財産権",
        list: [
          "カード画像、テキスト、デザイン、ロゴ、画面構成に関する権利は Daydream Factory または正当な権利者に帰属します。",
          "事前許可なくサービス内容を複製、配布、商用利用することはできません。",
        ],
      },
      {
        title: "4. サービス変更および中断",
        body:
          "Daydream Factory は、サービス改善、運営上の必要、外部 API やプラットフォーム方針の変更に応じて、機能変更や一部中断を行うことがあります。",
      },
      {
        title: "5. 免責",
        list: [
          "タロット解釈および AI 分析は、個人的な記録や振り返りを支える参考情報です。",
          "一部機能は、ストア方針、通信環境、外部 API の状態によって制限される場合があります。",
        ],
      },
      {
        title: "6. お問い合わせ",
        body: "本規約に関するお問い合わせ: support@daydreamfactory.net",
      },
    ],
  },
};

const LunaTarotTerms: React.FC = () => {
  const [locale, setLocale] = React.useState<Locale>("en");

  React.useEffect(() => {
    setLocale(detectPageLocale(LOCALE_STORAGE_KEY, locales, "en"));
  }, []);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const copy = content[locale];

  return (
    <main style={styles.page}>
      <div style={styles.shell}>
        <div style={styles.topRow}>
          <Link to="/" style={styles.homeLink}>
            ← Daydream Factory
          </Link>
          <div style={styles.topRowMeta}>
            <div style={styles.localeSwitch} aria-label="Language switcher">
              {locales.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setLocale(item)}
                  style={{
                    ...styles.localeButton,
                    ...(locale === item ? styles.activeLocaleButton : {}),
                  }}
                >
                  {localeLabels[item]}
                </button>
              ))}
            </div>
            <span style={styles.crumb}>{copy.crumb}</span>
          </div>
        </div>

        <section style={styles.hero}>
          <h1 style={styles.title}>
            {copy.title}
            <br />
            <span style={styles.accentTitle}>{copy.accentTitle}</span>
          </h1>
          <p style={styles.subtitle}>{copy.subtitle}</p>
        </section>

        {copy.sections.map((section) => (
          <section key={section.title} style={styles.section}>
            <h2 style={styles.h2}>{section.title}</h2>
            <div style={styles.sectionCard}>
              {section.body ? (
                <p style={styles.body}>
                  {section.body.includes("support@daydreamfactory.net") ? (
                    <>
                      {section.body.replace("support@daydreamfactory.net", "")}
                      <a href="mailto:support@daydreamfactory.net" style={styles.link}>
                        support@daydreamfactory.net
                      </a>
                    </>
                  ) : (
                    section.body
                  )}
                </p>
              ) : null}
              {section.list ? (
                <ul style={styles.list}>
                  {section.list.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          </section>
        ))}
      </div>
    </main>
  );
};

export default LunaTarotTerms;
